import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Container,
  createStyles,
  makeStyles,
  Paper,
  Typography,
} from '@material-ui/core';
import {
  reservationQuery,
  reservationService,
  sessionQuery,
  propertyQuery,
  Reservation,
  ReservationStatus,
} from '@lib/state';
import { useObservable, usePageTitle, Section } from '@lib/common';
import { corporateAccountQuery, corporateAccountService } from 'app/state';
import { Tile } from 'app/shared';
import {
  BulkChangeBillingInitiateDialog,
  BulkChangeBillingReservationFilter,
  BulkChangeBillingReservationFilters,
  BulkChangeBillingReservationList,
} from '../components';
import { neutralChipStyle } from '../bulk-change-billing-styles';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles(theme =>
  createStyles({
    summaryBar: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: theme.spacing(0.75),
      padding: theme.spacing(0.5, 1),
      marginBottom: theme.spacing(1),
      backgroundColor: 'rgba(0, 0, 0, 0.015)',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      fontSize: '0.75rem',
    },
    summaryChip: neutralChipStyle(theme),
    toolbarButton: {
      padding: 4,
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
      '&:hover': {
        color: theme.palette.primary.main,
      },
    },
    toolbarButtonActive: {
      color: theme.palette.primary.main,
    },
    stickyFooter: {
      position: 'sticky',
      bottom: 0,
      zIndex: 10,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: theme.spacing(1, 2),
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.paper,
    },
    helperText: {
      fontSize: '0.7rem',
      color: theme.palette.text.secondary,
      marginTop: 1,
    },
  })
);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const STATUS_LABEL: Record<string, string> = {
  [ReservationStatus.Open]: 'Open',
  [ReservationStatus.CheckedIn]: 'Checked In',
  [ReservationStatus.CheckedOut]: 'Checked Out',
  [ReservationStatus.Canceled]: 'Canceled',
  [ReservationStatus.NoShow]: 'No Show',
};

function useStatusCounts(reservations: Reservation[]) {
  return useMemo(() => {
    const counts: Record<string, number> = {};
    for (const r of reservations) {
      const s = r.status ?? 'Unknown';
      counts[s] = (counts[s] ?? 0) + 1;
    }
    return counts;
  }, [reservations]);
}

export const BulkChangeBillingSelectReservationsPage: React.FC<RouteComponentProps> = () => {
  usePageTitle('Bulk Change Billing — Select Reservations');

  const styles = useStyles();
  const history = useHistory();
  const propertyId = useObservable(sessionQuery.propertyId);
  const customerId = useObservable(propertyQuery.activeProperty)?.customerId;
  const reservations = useObservable(reservationQuery.searchResultsBulkChangeBilling);
  const pagination = useObservable(reservationQuery.pagination);
  const corporations = useObservable(corporateAccountQuery.corporations);

  const [filters, setFilters] = useState<BulkChangeBillingReservationFilters>({});
  const [selectedReservations, setSelectedReservations] = useState<Reservation[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);

  const statusCounts = useStatusCounts(reservations);
  const selectedCount = selectedReservations.length;
  const hasResults =
    reservations.length > 0 && !!filters.corporateAccountId && !!filters.start && !!filters.end;

  useEffect(() => {
    if (customerId) corporateAccountService.getAllCorporateAccounts(customerId);
  }, [customerId]);

  const onFilterChange = useCallback(
    (newFilters: BulkChangeBillingReservationFilters) => {
      setFilters(newFilters);
      setSelectedReservations([]);
      if (!propertyId) return;

      if (!newFilters.corporateAccountId || !newFilters.start || !newFilters.end) return;

      var limit = 20;
      if (!!newFilters.corporateAccountId) limit = 500;

      reservationService.getReservationsForBulkChangeBilling(
        {
          propertyId,
          corporateAccountId: newFilters.corporateAccountId,
          start: newFilters.start,
          end: newFilters.end,
        },
        undefined,
        limit
      );
    },
    [propertyId]
  );

  const loadMore = useCallback(() => {
    if (!propertyId) return;
    reservationService.getReservationsForBulkChangeBilling(
      {
        propertyId,
        corporateAccountId: filters.corporateAccountId,
        start: filters.start,
        end: filters.end,
      },
      pagination.continuationToken,
      undefined
    );
  }, [propertyId, filters, pagination]);

  if (!propertyId) return null;

  return (
    <Container maxWidth="xl">
      <Section>
        <Tile>
          {/* ---- Header ---- */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h1">Bulk Change Billing — Select Reservations</Typography>
            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => history.push('/bulk-change-billing')}
              >
                View Past Runs
              </Button>
            </Box>
          </Box>

          <BulkChangeBillingReservationFilter
            filters={filters}
            corporateAccounts={corporations}
            onChange={onFilterChange}
          />

          {hasResults && (
            <>
              {/* ---- Summary bar + toolbar ---- */}
              <div className={styles.summaryBar}>
                <Typography variant="body2" style={{ fontSize: 'inherit' }}>
                  <strong>{reservations.length}</strong> reservation
                  {reservations.length !== 1 ? 's' : ''}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ fontSize: 'inherit' }}>
                  ·
                </Typography>
                {Object.entries(statusCounts).map(([status, count]) => (
                  <Chip
                    key={status}
                    label={`${STATUS_LABEL[status] ?? status} (${count})`}
                    className={styles.summaryChip}
                    size="small"
                  />
                ))}
                <Box flexGrow={1} />
                <Typography
                  variant="body2"
                  color={selectedCount > 0 ? 'textPrimary' : 'textSecondary'}
                  style={{ fontSize: 'inherit' }}
                >
                  <strong>{selectedCount}</strong> selected
                </Typography>
              </div>

              {/* ---- Table ---- */}
              <BulkChangeBillingReservationList
                reservations={reservations}
                selectedReservations={selectedReservations}
                onSelectionChange={setSelectedReservations}
                isDone={pagination.isDone}
                loadMore={loadMore}
                effectiveStartDate={filters.start!}
                effectiveEndDate={filters.end!}
              />
            </>
          )}
        </Tile>
      </Section>

      {/* ---- Sticky footer (visible when results exist) ---- */}
      {hasResults && (
        <Paper className={styles.stickyFooter} elevation={3} square>
          <Box>
            <Typography variant="body2">
              <strong>{selectedCount}</strong> reservation{selectedCount !== 1 ? 's' : ''} selected
            </Typography>
            {selectedCount === 0 && (
              <Typography className={styles.helperText}>
                Select at least one reservation to continue.
              </Typography>
            )}
            {selectedCount > 0 && (
              <Typography className={styles.helperText}>
                You will review pricing changes before anything is applied.
              </Typography>
            )}
          </Box>
          <Button
            color="primary"
            variant="contained"
            disabled={selectedCount === 0}
            onClick={() => setDialogOpen(true)}
          >
            Review Selected Reservations
          </Button>
        </Paper>
      )}

      {propertyId && (
        <BulkChangeBillingInitiateDialog
          open={dialogOpen}
          propertyId={propertyId}
          reservationIds={selectedReservations.map(r => r.id)}
          selectedReservations={selectedReservations}
          corporateAccounts={(corporations ?? []).filter(ca => ca.isInvoiceEnabled)}
          effectiveStartDate={filters.start}
          effectiveEndDate={filters.end}
          onClose={() => setDialogOpen(false)}
          onSuccess={runId => history.push(`/bulk-change-billing/${runId}`)}
        />
      )}
    </Container>
  );
};
